import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Kicker } from './Kicker'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { emitEvent } from '@/lib/emitEvent'
import { nextDueVaccines } from '@/lib/cdcVaccines'
import type { Person, JSONValue } from '@/lib/types'

/**
 * VaccinesSection — shows next-due CDC-recommended vaccines for a baby/child based
 * on age. "Mark given" fires a medication.recorded event with payload.vaccine_name
 * so the schedule updates immediately.
 */

export function VaccinesSection({ person }: { person: Person }) {
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const [busy, setBusy] = useState<string | null>(null)

  // Read prior vaccine events so we know what's already given
  const givenQ = useQuery({
    enabled: !!familyId && !!person.id,
    queryKey: ['vaccines-given', person.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('payload')
        .eq('family_id', familyId!)
        .eq('person_id', person.id)
        .eq('event_type', 'early_care.medication.recorded')
        .is('supersedes_event_id', null)
        .limit(200)
      if (error) throw error
      const given = new Set<string>()
      for (const row of data ?? []) {
        const p = row.payload as Record<string, unknown> | null
        const vax = typeof p?.vaccine_name === 'string' ? (p.vaccine_name as string) : null
        const dose = typeof p?.vaccine_dose === 'number' ? (p.vaccine_dose as number) : null
        if (vax && dose != null) given.add(`${vax}:${dose}`)
      }
      return given
    },
  })

  const nextDue = useMemo(() => {
    if (!person.date_of_birth) return []
    return nextDueVaccines(new Date(person.date_of_birth), givenQ.data ?? new Set(), new Date(), 5)
  }, [person.date_of_birth, givenQ.data])

  async function markGiven(vaccine: string, dose: number) {
    if (!familyId || !user) return
    setBusy(`${vaccine}:${dose}`)
    try {
      await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType: 'early_care.medication.recorded',
        payload: {
          medication_name: `${vaccine} dose ${dose}`,
          amount: 1,
          amount_unit: 'other',
          route: 'other',
          confirmed_by_user_id: user.id,
          vaccine_name: vaccine,
          vaccine_dose: dose,
          source_note: 'CDC schedule mark-given',
        } as JSONValue,
        occurredAt: new Date(),
      })
      await givenQ.refetch()
    } finally {
      setBusy(null)
    }
  }

  if (nextDue.length === 0) return (
    <section>
      <Kicker>Vaccines</Kicker>
      <div className="mt-2 glass-tile p-4">
        <div className="text-body text-text-secondary">All caught up through 24 months. 🎉</div>
        <div className="mt-1 text-caption text-text-tertiary">
          Per CDC 0-24m schedule. Talk to your pediatrician for adolescent vaccines.
        </div>
      </div>
    </section>
  )

  return (
    <section>
      <Kicker>Vaccines up next (CDC 0-24m)</Kicker>
      <div className="mt-2 space-y-2">
        {nextDue.map((v) => {
          const key = `${v.vaccine}:${v.dose}`
          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className={`glass-tile p-3 flex items-center justify-between gap-3 ${v.overdue ? 'bloom-coral' : ''} relative overflow-hidden`}
            >
              <div className="relative z-10 flex-1 min-w-0">
                <div className="text-body font-medium text-text-primary truncate">
                  {v.vaccine} · dose {v.dose}
                </div>
                <div className="text-caption text-text-tertiary">
                  {v.overdue
                    ? `Overdue — was recommended by ${v.maxMonths}mo`
                    : v.monthsUntilDue <= 0
                      ? `Due now (recommended ${v.recommendedMonths}mo)`
                      : `In ~${v.monthsUntilDue.toFixed(1)} months (recommended ${v.recommendedMonths}mo)`}
                </div>
                {v.notes && <div className="text-tag text-text-quiet mt-1">{v.notes}</div>}
              </div>
              <button
                type="button"
                onClick={() => markGiven(v.vaccine, v.dose)}
                disabled={busy === key}
                className="relative z-10 pill-btn pill-btn--ghost shrink-0"
                style={{ minHeight: 36, padding: '8px 14px', fontSize: '12px' }}
              >
                {busy === key ? 'Saving…' : 'Mark given'}
              </button>
            </motion.div>
          )
        })}
      </div>
      <div className="mt-2 text-tag text-text-quiet">
        US CDC recommended schedule 0-24m. Talk to your pediatrician for actual timing.
      </div>
    </section>
  )
}
